import {
  DomesticAccommodationUseType,
  StatusCode,
  OrderCancelPreviewOrderDiscountDetails,
  OrderCancelCoupon,
} from '~/__generated__/__types__'
import { OrderDetailsV3Query } from '~/__generated__/app/query/order-details/order-details.graphql'
import { OrderCancelPreviewV2Query } from '~/__generated__/app/query/order-cancel-preview/order-cancel-preview.graphql'
import { OrderCompleteV3Query } from '~/__generated__/app/query/order-complete/order-complete.graphql'
import { OrderListV3Query } from '~/__generated__/app/query/order-list/order-list.graphql'
import { OrderCancelV3Mutation } from '~/__generated__/app/query/order-cancel/order-cancel.graphql'

type DomesticTrainOrderComplete = Extract<
  OrderCompleteV3Query['orderDetailsV3']['categoryOrders'][number],
  { __typename?: 'OrderDetailsTrain' }
>

type DomesticBusOrderComplete = Extract<
  OrderCompleteV3Query['orderDetailsV3']['categoryOrders'][number],
  { __typename?: 'OrderDetailsBus' }
>
type DomesticBusOrderDetail = Extract<
  OrderDetailsV3Query['orderDetailsV3']['categoryOrders'][number],
  { __typename?: 'OrderDetailsBus' }
>

type OrderListCategoryOrder =
  OrderListV3Query['orderListV3']['orders'][number]['categoryOrders'][number]
type OrderListCategoryOrderTrain = Extract<
  OrderListCategoryOrder,
  { __typename?: 'CartTrainOrder' }
>

export const domesticUseTypeToProductType = (type: string) => {
  const useTypeObject = {
    DAY_USE: 'R',
    OVER_NIGHT: 'S',
    MULTI_NIGHTS: 'D',
  }
  return useTypeObject[type] || ''
}

export const domesticProductTypeToUseType = (type: string) => {
  const productTypeObject = {
    R: DomesticAccommodationUseType.DayUse,
    S: DomesticAccommodationUseType.OverNight,
    D: DomesticAccommodationUseType.MultiNights,
  }
  return productTypeObject[type] || ''
}

export const domesticStatusCodeToStatusText = (status: StatusCode) => {
  const statusObject = {
    [StatusCode.PrepareReserve]: '예약준비',
    [StatusCode.CompleteReserve]: '예약완료',
    [StatusCode.FailReserve]: '예약실패',
    [StatusCode.PgCancel]: 'PG사강제취소',
    [StatusCode.CustomerCancel]: '고객취소',
    [StatusCode.AdminCancel]: '관리자취소',
    [StatusCode.CancelPointRefund]: '포인트취소',
    [StatusCode.OfflineCancel]: '현장취소', // offline 에서만 가능, 실제 쓰이진 않으
  }
  return statusObject[status] || ''
}

/**
 * PG, POINT 결제금액은 비율로 배분한다
 * - 숙소(20,000원) 와 레저티켓 2장(7,500원 * 2장 = 15,000원) 을 함께 구매하고 PG 25,000, 포인트 5,000, 숙소 쿠폰(5,000) 결제 했을 경우
 * - 분배 기준 금액
 * - 숙소 : 20,000 - 쿠폰사용금액 5,000 = 15,000
 * - 레저 : 15,000
 * - 총 구매금액 : 35,000 - 쿠폰사용금액 5,000 = 30,000
 * - 비중
 * - 숙소 : 15,000 / 총 구매금액 30,000 원 (쿠폰할인 5,000원 제외) = 50%
 * - 레저 : 15,000 / 총 구매금액 30,000 = 50%
 * - 숙소 : 50% (PG: 12,500, POINT: 2,500, COUPON: 5,000)
 * - 레저 : 50% (PG: 12,500, POINT: 2,500)
 */
export const paymentAmountByRatio = (
  orderAmount: number,
  itemAmount: number,
  pgAmount: number,
  pointAmount: number,
  couponAmount: number,
  membershipAmount: number
) => {
  const ratio =
    (itemAmount - (couponAmount + membershipAmount)) /
    (orderAmount - (couponAmount + membershipAmount))
  const itemPgAmount = Math.floor(pgAmount * ratio)
  const itemPointAmount = Math.floor(pointAmount * ratio)
  return { itemPgAmount, itemPointAmount }
}

export const orderAmountCategoryOrders = (
  orders: OrderCompleteV3Query['orderDetailsV3']['categoryOrders']
) => {
  return orders.reduce((acc, cur) => {
    const categoryOrderAmount =
      cur.__typename === 'OrderDetailsDomestic'
        ? cur.price
        : cur.__typename === 'OrderDetailsLeisureProduct'
          ? cur.items.reduce((acc, cur) => acc + cur.price, 0)
          : cur.__typename === 'OrderDetailsTrain'
            ? cur.tickets.reduce((acc, cur) => acc + cur.price, 0)
            : cur.__typename === 'OrderDetailsBus'
              ? cur.reservations.reduce(
                  (acc, cur) => acc + cur.tickets.reduce((acc, cur) => acc + cur.price, 0),
                  0
                )
              : 0
    return acc + categoryOrderAmount
  }, 0)
}

export const couponAmountCategoryOrders = (
  orders: OrderCompleteV3Query['orderDetailsV3']['categoryOrders']
) => {
  return orders.reduce((acc, cur) => {
    const categoryCouponAmount =
      cur.__typename === 'OrderDetailsDomestic'
        ? cur.domesticDiscount?.coupons.reduce((acc, cur) => acc + cur.amount, 0) || 0
        : cur.__typename === 'OrderDetailsLeisureProduct'
          ? cur.leisureDiscount?.coupons.reduce((acc, cur) => acc + cur.amount, 0) || 0
          : cur.__typename === 'OrderDetailsTrain'
            ? cur.trainDiscount?.coupons.reduce((acc, cur) => acc + cur.amount, 0) || 0
            : cur.__typename === 'OrderDetailsBus'
              ? cur.busDiscount?.coupons.reduce((acc, cur) => acc + cur.amount, 0) || 0
              : 0
    return acc + categoryCouponAmount
  }, 0)
}

export const couponAmountDetailCategoryOrders = (
  orders: OrderDetailsV3Query['orderDetailsV3']['categoryOrders']
) => {
  return orders.reduce((acc, cur) => {
    const categoryCouponAmount =
      cur.__typename === 'OrderDetailsDomestic'
        ? cur.discount?.coupons.reduce((acc, cur) => acc + cur.amount, 0) || 0
        : cur.__typename === 'OrderDetailsLeisureProduct'
          ? cur.discount?.coupons.reduce((acc, cur) => acc + cur.amount, 0) || 0
          : cur.__typename === 'OrderDetailsTrain'
            ? cur.discount?.coupons.reduce((acc, cur) => acc + cur.amount, 0) || 0
            : cur.__typename === 'OrderDetailsBus'
              ? cur.discount?.coupons.reduce((acc, cur) => acc + cur.amount, 0) || 0
              : 0
    return acc + categoryCouponAmount
  }, 0)
}

export const membershipAmountCategoryOrders = (
  orders: OrderCompleteV3Query['orderDetailsV3']['categoryOrders']
) => {
  return orders.reduce((acc, cur) => {
    const categoryMembershipAmount =
      cur.__typename === 'OrderDetailsDomestic'
        ? cur?.domesticDiscount?.mobileMembership?.amount || 0
        : 0
    return acc + categoryMembershipAmount
  }, 0)
}

export const membershipAmountDetailCategoryOrders = (
  orders: OrderDetailsV3Query['orderDetailsV3']['categoryOrders']
) => {
  return orders.reduce((acc, cur) => {
    const categoryMembershipAmount =
      cur.__typename === 'OrderDetailsDomestic' ? cur?.discount?.mobileMembership?.amount || 0 : 0
    return acc + categoryMembershipAmount
  }, 0)
}

export const getPackageDiscountCompleteCategoryOrders = (
  order: OrderDetailsV3Query['orderDetailsV3'] | OrderCompleteV3Query['orderDetailsV3']
) => {
  return order.discount?.package?.amount ?? 0
}

export const orderPayment = (order: OrderCompleteV3Query['orderDetailsV3']) => {
  const payments = order.payments
  const categoryOrders = order.categoryOrders
  const pg = payments.find(payment => payment.type === 'PG')
  const point = payments.find(payment => payment.type === 'POINT')
  const nolPoint = payments.find(payment => payment.type === 'NOL_POINT')
  return {
    orderAmount: orderAmountCategoryOrders(categoryOrders),
    pgType: pg?.type ?? '',
    pgMethod: pg?.method ?? '',
    pgAmount: pg?.amount ?? 0,
    pointAmount: point?.amount ?? 0,
    nolPointAmount: nolPoint?.amount ?? 0,
    couponAmount: couponAmountCategoryOrders(categoryOrders),
    membershipAmount: membershipAmountCategoryOrders(categoryOrders),
    packageDiscountAmount: getPackageDiscountCompleteCategoryOrders(order),
  }
}

export const orderDetailPayment = (order: OrderDetailsV3Query['orderDetailsV3']) => {
  const payments = order.payments
  const categoryOrders = order.categoryOrders
  const pg = payments.find(payment => payment.type === 'PG')
  const point = payments.find(payment => payment.type === 'POINT')
  const nolPoint = payments.find(payment => payment.type === 'NOL_POINT')
  return {
    orderAmount: orderAmountCategoryOrders(categoryOrders),
    pgType: pg?.type ?? '',
    pgMethod: pg?.method ?? '',
    pgAmount: pg?.amount ?? 0,
    pointAmount: point?.amount ?? 0,
    nolPointAmount: nolPoint?.amount ?? 0,
    couponAmount: couponAmountDetailCategoryOrders(categoryOrders),
    membershipAmount: membershipAmountDetailCategoryOrders(categoryOrders),
    packageDiscountAmount: getPackageDiscountCompleteCategoryOrders(order),
  }
}

export const couponRefundCategoryOrders = (
  order: OrderCancelPreviewV2Query['orderCancelPreviewV2'] | OrderCancelV3Mutation['orderCancelV3']
) => {
  let refundCoupons: Array<OrderCancelCoupon | OrderCancelPreviewOrderDiscountDetails> = []
  if (order.__typename === 'OrderCancelPreview') {
    const coupons = order.categoryOrders
      ?.map(order => order.discount?.coupons)
      .flat()
      .filter(v => v)
    if (coupons?.length) {
      /**
       * 하나라도 반환하지 않는 케이스가 있다면 false 리턴
       */
      refundCoupons = (coupons as OrderCancelPreviewOrderDiscountDetails[]).filter(
        coupon => !!coupon?.refund
      )
    }
  } else if (order.__typename === 'OrderCancel') {
    const coupons = order.categoryOrders
      .map(order => order.discount?.coupons)
      .flat()
      .filter(v => v)
    if (coupons.length) {
      /**
       * 하나라도 반환하지 않는 케이스가 있다면 false 리턴
       */
      refundCoupons = (coupons as OrderCancelCoupon[]).filter(coupon => !!coupon?.refund)
    }
  }
  /**
   * 쿠폰을 사용하지 않는 경우 false 리턴
   */
  return {
    refundCoupon: !!refundCoupons.length,
    refundCouponAmount: refundCoupons.reduce((sum, coupon) => sum + coupon.commissionAmount, 0),
  }
}

export const membershipRefundCategoryOrders = (
  order: OrderCancelPreviewV2Query['orderCancelPreviewV2'] | OrderCancelV3Mutation['orderCancelV3']
) => {
  if (order.__typename === 'OrderCancelPreview') {
    const mobileMemberships = order.categoryOrders
      ?.map(order => order.discount?.mobileMembership)
      .filter(v => v)
    if (mobileMemberships?.length) {
      /**
       * 하나라도 반환하지 않는 케이스가 있다면 false 리턴
       */
      return mobileMemberships.filter(membership => membership?.refund === false).length <= 0
    }
  } else if (order.__typename === 'OrderCancel') {
    const mobileMemberships = order.categoryOrders
      .map(order => {
        if (order.__typename === 'OrderCancelDomesticOrder') {
          return order.discount?.mobileMembership
        }
        return null
      })
      .filter(v => v)
    if (mobileMemberships.length) {
      /**
       * 하나라도 반환하지 않는 케이스가 있다면 false 리턴
       */
      return mobileMemberships.filter(membership => membership?.refund === false).length <= 0
    }
  }
  /**
   * mobileMembership 을 사용하지 않는 경우 false 리턴
   */
  return false
}

export const orderRefund = (
  order: OrderCancelPreviewV2Query['orderCancelPreviewV2'] | OrderCancelV3Mutation['orderCancelV3']
) => {
  if (order.__typename === 'OrderCancelPreview') {
    const pg = order.payments?.find(payment => payment.type === 'PG')
    const point = order.payments?.find(payment => payment.type === 'POINT')
    const nolPoint = order.payments?.find(payment => payment.type === 'NOL_POINT')
    return {
      refundAmount: order.payments?.reduce((acc, cur) => acc + cur.amount, 0),
      pgType: pg?.type ?? '',
      pgRefund: pg?.amount ?? 0,
      pointRefund: point?.amount ?? 0,
      nolPointRefund: nolPoint?.amount ?? 0,
      couponRefund: couponRefundCategoryOrders(order),
      membershipRefund: membershipRefundCategoryOrders(order),
    }
  } else if (order.__typename === 'OrderCancel') {
    const pg = order.payments?.find(payment => payment.type === 'PG')
    const point = order.payments?.find(payment => payment.type === 'POINT')
    const nolPoint = order.payments?.find(payment => payment.type === 'NOL_POINT')
    return {
      refundAmount: order.payments?.reduce((acc, cur) => acc + cur.amount, 0),
      pgType: pg?.method ?? '',
      pgRefund: pg?.amount ?? 0,
      pointRefund: point?.amount ?? 0,
      nolPointRefund: nolPoint?.amount ?? 0,
      couponRefund: couponRefundCategoryOrders(order),
      membershipRefund: membershipRefundCategoryOrders(order),
    }
  }
}

export const trainMetaFromOrderListTickets = (order: OrderListCategoryOrderTrain) => {
  const tickets = order.tickets
  return tickets.map(ticket => ({
    departureStationCode: ticket.departureStationCode,
    departureStation: ticket.departureStationName,
    arrivalStation: ticket.arrivalStationName,
    arrivalStationCode: ticket.arrivalStationCode,
    trainClassifyNo: ticket.stationTrainClassifyCode,
    trainNo: ticket.trainNo,
    roundtripLeg: order.tickets.length > 1 ? 'return' : 'outbound',
    tripType: order.tickets.length > 1 ? 'ROUND' : 'ONEWAY',
    trainSeatSet: ticket.seats.map(seat => ({
      carriageNo: seat.carriageNo,
      seatDescription: seat.seatDescription,
      trainCarriageName: `${+seat.carriageNo}호차`,
      trainSeatName: seat.seatDescription,
    })),
  }))
}

export const trainMetaFromOrderCompleteTickets = (order: DomesticTrainOrderComplete) => {
  const tickets = order.tickets
  return tickets.map(ticket => ({
    departureStationCode: ticket.departureStationCode,
    departureStation: ticket.departureStationName,
    arrivalStation: ticket.arrivalStationName,
    arrivalStationCode: ticket.arrivalStationCode,
    trainClassifyNo: ticket.stationTrainClassifyCode,
    trainNo: ticket.trainNo,
    roundtripLeg: order.tickets.length > 1 ? 'return' : 'outbound',
    tripType: order.tickets.length > 1 ? 'ROUND' : 'ONEWAY',
    trainSeatSet: ticket.seats.map(seat => ({
      carriageNo: seat.carriageNo,
      seatDescription: seat.seatDescription,
      trainCarriageName: `${+seat.carriageNo}호차`,
      trainSeatName: seat.seatDescription,
    })),
  }))
}

export const busMetaFromOrderCompleteTickets = (order: DomesticBusOrderComplete) => {
  return order.reservations.map(reservation => ({
    returnTrip: reservation.returnTrip,
    departureDate: reservation.departureDate,
    departureTime: reservation.departureTime,
    departureTerminalNo: reservation.departureTerminalNo,
    arrivalTerminalNo: reservation.arrivalTerminalNo,
    transportationCompanyCode: reservation.transportationCompanyCode,
    busGrade: reservation.busGrade,
    seats: reservation.tickets.map(ticket => {
      return {
        seatNo: ticket.seatNo,
        ticketType: ticket.ticketType,
      }
    }),
    price: reservation.tickets.reduce((acc, cur) => acc + +cur.price, 0),
    busDiscountAmount: order.busDiscount?.busDiscount?.amount ?? 0,
  }))
}

export const busMetaFromOrderDetailTickets = (order: DomesticBusOrderDetail) => {
  return order.reservations.map(reservation => ({
    returnTrip: reservation.returnTrip,
    departureDate: reservation.departureDate,
    departureTime: reservation.departureTime,
    departureAt: reservation.departureAt,
    departureTerminalNo: reservation.departureTerminalNo,
    departureTerminalName: reservation.departureTerminalName,
    expectedArriveAt: reservation.expectedArriveAt,
    arrivalTerminalNo: reservation.arrivalTerminalNo,
    arrivalTerminalName: reservation.arrivalTerminalName,
    discountType: order.discount?.busDiscount?.discountType ?? 'NA',
    busGrade: reservation.busGrade,
    transportationCompanyCode: reservation.transportationCompanyCode,
    routerReservationNo: reservation.routerReservationNo,
    boardingNo: reservation.boardingNo,
    tickets: reservation.tickets.map(ticket => {
      return {
        status: ticket.status,
        ticketType: ticket.ticketType,
        seatNo: ticket.seatNo,
        price: ticket.price,
        discountAmount: 0, // 시외 우등 할인 제외로 0
        routerTicketNo: ticket.routerTicketNo,
        qrGenNo: ticket.qrGenNo,
        confirm: ticket.confirm,
        confirmedAt: ticket.confirmedAt,
      }
    }),
    price: reservation.tickets.reduce((acc, cur) => acc + +cur.price, 0),
    busDiscountAmount: order.discount?.busDiscount?.amount ?? 0,
  }))
}
